import type {Product, Facet, SortOption} from "@apptus/esales-api";

import {useRoute, useState} from "nuxt/app";

interface ElevateProductListStateParams {
	categoryId?: string;
	query?: string;
	origin?: string;
}
/**
 * Composable to manage and access Elevate product list state. Useful because sometimes you may want to access the already-loaded elevate product listing from outside of the elevate-product-list component.
 */
export function useElevateProductListState(
	params: ElevateProductListStateParams,
) {
	const route = useRoute();

	// NOTE: This doesn't get really updated properly if client-side navigation happens and another elevate list component gets rendered to replace the previous one - the new one will use the previous page's list. We handle refreshing the list on the elevate list component's side though, so even if the list is stored in a previous page's key, it doesn't matter.
	const stateBaseKey = `elevate-product-list.${route.fullPath}.${params.origin ?? ""}.${params.query ?? ""}`;

	const stateKeys = {
		products: `${stateBaseKey}.products`,
		isLoading: `${stateBaseKey}.isLoading`,
		alternativeProducts: `${stateBaseKey}.alternativeProducts`,
		facets: `${stateBaseKey}.facets`,
		productCount: `${stateBaseKey}.productCount`,
		sortOptions: `${stateBaseKey}.sortOptions`,
		resetProducts: `${stateBaseKey}.resetProducts`,
	};

	const products = useState<Product[]>(stateKeys.products, () => []);
	const isLoading = useState<boolean>(stateKeys.isLoading, () => true);
	const alternativeProducts = useState<Product[]>(
		stateKeys.alternativeProducts,
		() => [],
	);
	const facets = useState<Facet[]>(stateKeys.facets, () => []);
	const productCount = useState(stateKeys.productCount, () => 0);
	const sortOptions = useState<SortOption[]>(stateKeys.sortOptions, () => []);
	const resetProducts = useState(stateKeys.resetProducts, () => true);

	const resetState = () => {
		clearNuxtState(Object.values(stateKeys), {reset: true});
	};

	return {
		products,
		isLoading,
		alternativeProducts,
		facets,
		productCount,
		sortOptions,
		resetProducts,
		stateKeys,
		stateBaseKey,
		resetState,
	};
}
